import { Link } from '@mui/material';

import {
  Column,
  ColumnGroup,
  EllipsisWithTooltip,
  Icons,
  TableHeader,
} from '@cast/design-system';

import { RebalanceMigrationStatusChip } from 'components/chips';
import { Link as RouterLink } from 'components/router';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import {
  makeNamespaceCostMonitoringLink,
  makeWorkloadEfficiencyCostMonitoringLink,
} from 'features/cluster/cost-report/links';
import { WorkloadResourceRequestCell } from 'features/cluster/nodes/_components/WorkloadResourceRequestCell';
import { WorkloadBaseRunningInNode } from 'features/cluster/nodes/types';
import { Cluster } from 'types/cluster';

import { WorkloadIssuesTooltip } from './WorkloadIssuesTooltip';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

type Props = {
  clusterId: Cluster['id'];
  /** @see WorkloadsTabContent.fillParentHeight */
  fillParentHeight?: boolean;
};

export const WorkloadsTable = ({
  clusterId,
  fillParentHeight = true,
}: Props) => {
  const isCardEmbed = !fillParentHeight;

  return (
    <SearchableTable
      testId="node-details-workloads-table"
      fillParentHeight={fillParentHeight}
      // Card/overview embed has no fixed parent height; cap so long lists scroll.
      {...(!fillParentHeight && { maxHeight: 480 })}
      rowHeight={40}
      noShadow={isCardEmbed}
      sx={isCardEmbed ? { minWidth: 0, width: '100%' } : undefined}
      components={{
        noData: (
          <NoData
            testId="node-details-workloads-table-no-data"
            icon={<Icons.CastNode />}
            entity="workload"
          />
        ),
        noResults: (
          <NoResults
            testId="node-details-workloads-table-no-results"
            icon={<Icons.CastNode />}
            entity="workload"
          />
        ),
      }}
      cacheKey={tableCacheKeys.NODES.NODE_DETAILS_WORKLOADS}
      // Card title already shows "N Workloads" (Figma); skip duplicate count header.
      outerHeader={
        isCardEmbed ? undefined : (
          <TableHeader
            recordsCount={<SearchableTableRecordsCount title="workload" />}
          />
        )
      }
    >
      <Column<WorkloadBaseRunningInNode>
        id="name"
        header="workload"
        minWidth={200}
        renderCell={(workload) => (
          <Link
            data-testid="workload-name-link"
            component={RouterLink}
            to={makeWorkloadEfficiencyCostMonitoringLink({
              clusterId,
              workloadName: workload.name,
              namespace: workload.namespace,
              workloadType: workload.workloadType,
            })}
            underline="none"
            noWrap
            target="_blank"
          >
            <EllipsisWithTooltip
              TypographyProps={{
                variant: 'P12M',
              }}
            >
              {workload.name}
            </EllipsisWithTooltip>
          </Link>
        )}
        sortable
        defaultSort="asc"
        resizable
      />
      <Column<WorkloadBaseRunningInNode>
        id="workloadType"
        header="type"
        width={120}
        sortable
        resizable
      />
      <Column<WorkloadBaseRunningInNode>
        id="podsOnNode"
        header="pods on the node"
        width={185}
        sortable
        accessor="replicasPerNode"
        resizable
        renderCell={({
          replicasPerNode,
          replicasOnAllNodes: totalReplicas,
        }) => {
          return (
            <>
              {replicasPerNode}/{totalReplicas}
            </>
          );
        }}
      />

      <Column<WorkloadBaseRunningInNode>
        id="namespace"
        header="namespace"
        minWidth={140}
        sortable
        resizable
        renderCell={({ namespace }) => {
          return (
            <Link
              data-testid="workload-namespace-link"
              component={RouterLink}
              to={makeNamespaceCostMonitoringLink({
                clusterId,
                namespace,
              })}
              underline="none"
              noWrap
              target="_blank"
            >
              <EllipsisWithTooltip>{namespace}</EllipsisWithTooltip>
            </Link>
          );
        }}
      />

      <ColumnGroup id="resource-requests" title="Resource requests">
        <Column<WorkloadBaseRunningInNode>
          id="requestedCpu"
          header="CPU"
          width={140}
          sortable
          accessor="resourcesPerNode.requested.milliCpu"
          resizable
          infoTooltip="CPU cores reserved by CPU requests of pods running on the node"
          renderCell={(workload: WorkloadBaseRunningInNode) => {
            return (
              <WorkloadResourceRequestCell workload={workload} resource="cpu" />
            );
          }}
        />
        <Column<WorkloadBaseRunningInNode>
          id="requestedMemory"
          header="Memory"
          width={140}
          sortable
          accessor="resourcesPerNode.requested.memoryBytes"
          resizable
          infoTooltip="Memory reserved by Memory requests of pods running on the node"
          renderCell={(workload: WorkloadBaseRunningInNode) => {
            return (
              <WorkloadResourceRequestCell
                workload={workload}
                resource="memory"
              />
            );
          }}
        />
      </ColumnGroup>
      <Column<WorkloadBaseRunningInNode>
        id="migrationStatus"
        header="Bin packing ready"
        width={200}
        sortable
        resizable
        renderCell={(workload: WorkloadBaseRunningInNode) => {
          return (
            <WorkloadIssuesTooltip workload={workload}>
              <RebalanceMigrationStatusChip
                status={workload.migrationStatus}
                testId="bin-packing-status-chip"
              />
            </WorkloadIssuesTooltip>
          );
        }}
      />
    </SearchableTable>
  );
};
